import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../features/auth/AuthContext";
import { useSeasonContext } from "../../context/SeasonContext";
import logo from "../../assets/images/logo.png";

const toProperCase = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const DropdownMenu = ({ label, onClose, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLinkClick = () => {
    setIsOpen(false);
    onClose?.();
  };

  return (
    <li className="nav-dropdown" ref={ref}>
      <button
        className="nav-dropdown-toggle"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        {label} <span className="nav-dropdown-caret">&#9662;</span>
      </button>
      {isOpen && (
        <ul className="nav-dropdown-menu">{children(handleLinkClick)}</ul>
      )}
    </li>
  );
};

const SidebarToggle = ({ isOpen, onToggle }) => (
  <button
    className="menu-toggle sidebar-toggle"
    onClick={onToggle}
    aria-expanded={isOpen}
    aria-label="Toggle navigation"
  >
    <span className="sr-only">Toggle navigation</span>
    <div className="hamburger">
      <span></span>
      <span></span>
      <span></span>
    </div>
  </button>
);

/**
 * Slim top bar: logo, an (mobile-only) sidebar toggle for logged-in
 * users, the season selector, and an account menu. Primary page
 * navigation now lives in <Sidebar />, not here.
 */
const Header = ({ isSidebarOpen, onToggleSidebar }) => {
  const { isAdmin, hasProfile, isAuthenticated, logout, loading, user } =
    useAuth();
  const { currentSeason, setCurrentSeason, seasons } = useSeasonContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = () => {
    logout();
    closeMenu();
  };

  const handleSeasonChange = (e) => {
    setCurrentSeason(e.target.value);
  };

  const displayName = toProperCase(user?.displayName || user?.username || "Me");

  // The header can wrap onto extra lines on mobile (season selector,
  // account menu, and the account dropdown all add height), so its real
  // height isn't a fixed number. Measure it and publish the result as a
  // CSS variable so the sidebar/backdrop (and body padding) can offset
  // themselves from the header's *actual* height instead of a guessed
  // constant - otherwise the header can grow taller than that guess and
  // cover the top of the mobile menu.
  const headerRef = useRef(null);

  useLayoutEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl) return undefined;

    const updateHeaderHeight = () => {
      document.documentElement.style.setProperty(
        "--header-height",
        `${headerEl.offsetHeight}px`,
      );
    };

    updateHeaderHeight();

    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(headerEl);
    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, [isMenuOpen, isAuthenticated, loading]);

  if (loading || isAuthenticated === null) {
    return (
      <header className="main-header" ref={headerRef}>
        <div className="header-left">
          <div className="logo">
            <Link to="/">
              <img src={logo} alt="Premier League Predictions" />
            </Link>
          </div>
          <div className="site-title">
            <Link to="/">PLPP</Link>
          </div>
        </div>
        <nav className="main-nav">
          <ul>
            <li>
              <span>Loading...</span>
            </li>
          </ul>
        </nav>
      </header>
    );
  }

  // Logged-out shell keeps the original simple nav (How It Works /
  // Login / Register) — there's no sidebar to show these users.
  if (!isAuthenticated) {
    return (
      <header className="main-header" ref={headerRef}>
        <div className="header-left">
          <div className="logo">
            <Link to="/">
              <img src={logo} alt="Premier League Predictions" />
            </Link>
          </div>
          <div className="site-title">
            <Link to="/">PLPP</Link>
          </div>
        </div>

        <button className="menu-toggle" onClick={toggleMenu}>
          <span className="sr-only">Toggle menu</span>
          <div className="hamburger">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        <div className="header-right">
          <nav className={`main-nav ${isMenuOpen ? "is-open" : ""}`}>
            <ul>
              <li>
                <Link to="/how-it-works" onClick={closeMenu}>
                  How It Works
                </Link>
              </li>
              <li>
                <Link to="/login" onClick={closeMenu}>
                  Login
                </Link>
              </li>
              <li>
                <Link to="/register" onClick={closeMenu}>
                  Register
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </header>
    );
  }

  // Logged-in shell: slim bar, sidebar toggle on mobile, account menu.
  return (
    <header className="main-header" ref={headerRef}>
      <div className="header-left">
        <SidebarToggle isOpen={isSidebarOpen} onToggle={onToggleSidebar} />
        <div className="logo">
          <Link to="/">
            <img src={logo} alt="Premier League Predictions" />
          </Link>
        </div>
        <div className="site-title">
          <Link to="/">PLPP</Link>
        </div>
      </div>

      <div className="header-right">
        {!isAdmin && hasProfile && seasons.length > 1 && (
          <div className="season-selector">
            <label htmlFor="season-select">Season:</label>
            <select
              id="season-select"
              value={currentSeason || ""}
              onChange={handleSeasonChange}
              className="season-dropdown"
            >
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <nav className="main-nav account-nav">
          <ul>
            <DropdownMenu label={displayName} onClose={closeMenu}>
              {(handleLinkClick) => (
                <>
                  {!isAdmin && (
                    <li>
                      <Link to="/profile" onClick={handleLinkClick}>
                        Profile
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link to="/account" onClick={handleLinkClick}>
                      Account
                    </Link>
                  </li>
                  <li>
                    <Link to="/" onClick={handleLogout}>
                      Logout
                    </Link>
                  </li>
                </>
              )}
            </DropdownMenu>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
